import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function PhilosophyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '私たちの想い | おかやま健康製薬株式会社',
    description: 'おかやま健康製薬の企業理念、代表取締役岡山太郎のご挨拶、製品コンセプト、企業ストーリーをご紹介。家族の健康を守るという想いのもと、科学的根拠に基づいた高品質サプリメントを提供し続けています。',
    url: `${siteUrl}/philosophy`,
    inLanguage: 'ja-JP',
    isPartOf: {
      '@type': 'WebSite',
      name: 'おかやま健康製薬株式会社',
      url: siteUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '私たちの想い',
          item: `${siteUrl}/philosophy`,
        },
      ],
    },
    mainEntity: {
      '@type': 'Person',
      name: '岡山太郎',
      jobTitle: '代表取締役',
      worksFor: {
        '@type': 'Organization',
        name: 'おかやま健康製薬株式会社',
      },
    },
  };

  const concepts = [
    {
      id: 1,
      icon: 'ri-heart-pulse-line',
      title: '科学的根拠に基づく開発',
      description:
        '最新の栄養学研究と臨床データに基づき、効果が実証された成分のみを使用。すべての製品は科学的エビデンスに裏付けられています。',
    },
    {
      id: 2,
      icon: 'ri-shield-check-line',
      title: '徹底した品質管理',
      description:
        '原材料の選定から製造、出荷まで、すべての工程で厳格な品質管理を実施。安心・安全な製品をお届けすることが私たちの約束です。',
    },
    {
      id: 3,
      icon: 'ri-team-line',
      title: '全世代へのサポート',
      description:
        '10代の成長期から高齢者まで、それぞれのライフステージに応じた最適なサプリメントを提供。家族全員の健康を一社でサポートします。',
    },
    {
      id: 4,
      icon: 'ri-leaf-line',
      title: '天然由来成分へのこだわり',
      description:
        '自然の恵みを最大限に活かし、厳選された天然由来成分を中心に配合。身体に優しく、長期間安心してお飲みいただける製品づくりを追求しています。',
    },
    {
      id: 5,
      icon: 'ri-flask-line',
      title: '独自の吸収技術',
      description:
        '有効成分を効率よく体内に届けるため、独自の吸収促進技術を開発。一般的なサプリメントと比較して高い吸収率を実現しています。',
    },
    {
      id: 6,
      icon: 'ri-customer-service-2-line',
      title: 'お客様との対話',
      description:
        'お客様の声を製品開発に反映するため、定期的なアンケートや健康相談窓口を設置。一人ひとりの健康課題に寄り添った製品改良を続けています。',
    },
  ];

  const timeline = [
    {
      year: '2000年',
      title: '会社設立',
      description:
        '「家族の健康を守る」という理念のもと、岡山市にておかやま健康製薬株式会社を設立。地域密着型の健康サプリメント製造を開始。',
    },
    {
      year: '2005年',
      title: '製品ラインナップ拡充',
      description:
        'シニア世代向けサプリメントの開発に成功。関節サポート成分を配合した「アクティブシニアサポート」が好評を博す。',
    },
    {
      year: '2010年',
      title: '品質管理体制の強化',
      description:
        'ISO認証取得により、国際基準の品質管理体制を確立。より安心・安全な製品提供を実現。',
    },
    {
      year: '2015年',
      title: '全世代対応製品の完成',
      description:
        '若年層向け、働き盛り世代向け、シニア世代向けの3タイプを主力商品として確立。全世代の健康をサポートする体制が整う。',
    },
    {
      year: '2020年',
      title: 'オンライン販売開始',
      description:
        '全国のお客様により便利にご利用いただけるよう、オンライン販売を開始。岡山から全国へ製品をお届け。',
    },
    {
      year: '2024年',
      title: '新製品開発',
      description:
        '働き盛り世代向け新商品「エナジーサポートプラス」を発売。現代人の健康課題に対応した製品開発を継続。',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="私たちの想い | 代表挨拶・企業理念 | おかやま健康製薬株式会社"
        description="おかやま健康製薬の企業理念、代表取締役岡山太郎のご挨拶、製品コンセプト、企業ストーリーをご紹介。家族の健康を守るという想いのもと、科学的根拠に基づいた高品質サプリメントを提供し続けています。岡山から全国へ、安心・安全な製品をお届けします。"
        keywords="岡山,企業理念,代表挨拶,製品コンセプト,企業ストーリー,おかやま健康製薬"
        canonical={`${siteUrl}/philosophy`}
        schema={aboutSchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[40vh] mt-16 lg:mt-20 bg-gradient-to-br from-[#4A90E2] to-[#2A6D8D] flex items-center justify-center">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/73d6301c9a30ff2c2f42fda8be3d50e6.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            私たちの想い
          </h1>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">
              製品コンセプト
            </h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto mb-6"></div>
            <p className="text-sm lg:text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
              おかやま健康製薬は、「科学」と「自然」の融合を大切にしています。最先端の研究成果と天然素材の力を組み合わせ、すべてのご家族が安心して毎日お飲みいただける製品を追求し続けています。
            </p>
          </div>

          {/* メインビジュアル */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-20">
            <div className="w-full h-72 sm:h-80 lg:h-96 rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=japanese%20female%20scientist%20in%20white%20lab%20coat%20carefully%20examining%20supplement%20capsules%20in%20modern%20clean%20pharmaceutical%20laboratory%20with%20advanced%20equipment%20bright%20lighting%20professional%20quality%20control%20inspection%20scene%20high%20resolution%20photography&width=700&height=500&seq=concept_lab1&orientation=landscape"
                alt="品質管理を行う研究員"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D] mb-4">
                お客様の健康を第一に考えた製品づくり
              </h3>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                私たちの製品づくりは、お客様の「健康でありたい」という想いに応えることから始まります。原材料の産地選定から最終製品の出荷まで、一切の妥協を許さない姿勢で取り組んでいます。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                岡山の自然豊かな環境で育まれた素材を活かしながら、最新の栄養科学に基づいた配合設計を行い、お子様からご高齢の方まで安心してお飲みいただける製品を生み出しています。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                すべての製品は、社内の研究チームによる厳格な試験を経て、効果と安全性が確認されたもののみを販売しています。
              </p>
            </div>
          </div>

          {/* コンセプトカード */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12 lg:mb-20">
            {concepts.map((concept) => (
              <article
                key={concept.id}
                className="bg-[#F5FAFE] rounded-3xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-2xl mb-6">
                  <i className={`${concept.icon} text-4xl text-[#4A90E2]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A4D6D] mb-4">{concept.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{concept.description}</p>
              </article>
            ))}
          </div>

          {/* 下部ビジュアル */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D] mb-4">
                家族みんなの笑顔のために
              </h3>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                健康は、家族の笑顔の源です。私たちは、成長期のお子様に必要な栄養素、働き盛り世代の活力をサポートする成分、シニア世代の健やかな毎日を支える素材を、それぞれ最適なバランスで配合しています。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                「家族全員が同じブランドのサプリメントを安心して飲める」——それが、おかやま健康製薬が目指す製品コンセプトの根幹です。世代を超えて信頼される製品を、これからもお届けしてまいります。
              </p>
            </div>
            <div className="order-1 lg:order-2 w-full h-72 sm:h-80 lg:h-96 rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/1c1be060b03af8ee1866b0e799d8b0d9.jpeg"
                alt="三世代の日本人家族"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CEO Message Section */}
      <section className="py-12 lg:py-20 bg-[#F5FAFE]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="w-full h-80 sm:h-[420px] lg:h-[500px]">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/619ff6bdf88293e06a9e9c7c2283f586.jpeg"
                alt="代表取締役 岡山太郎"
                className="w-full h-full object-cover object-top rounded-3xl shadow-2xl"
              />
            </div>
            <div>
              <div className="mb-6">
                <span className="text-[#4A90E2] font-medium text-sm lg:text-base">
                  代表取締役ご挨拶
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A4D6D] mt-2">
                  岡山 太郎
                </h2>
              </div>
              <div className="space-y-6 text-sm lg:text-base text-gray-700 leading-relaxed">
                <p>
                  おかやま健康製薬株式会社のホームページをご覧いただき、誠にありがとうございます。代表取締役の岡山太郎と申します。
                </p>
                <p>
                  私たちは2000年の創業以来、「家族の健康を守る」という一貫した理念のもと、高品質なサプリメントの開発・製造に取り組んでまいりました。健康は人生の基盤であり、毎日の小さな選択の積み重ねが、将来の健やかな生活を作り上げていくと信じています。
                </p>
                <p>
                  現代社会では、年齢や生活環境によって必要な栄養素が大きく異なります。成長期の子供たち、仕事に励む働き盛り世代、人生を謳歌するシニア世代。それぞれのライフステージに応じた最適なサプリメントを提供することが、私たちの使命です。
                </p>
                <p>
                  岡山の地から全国のご家庭へ、科学的根拠に基づいた安心・安全な製品をお届けし続けることで、皆様の健康で豊かな人生をサポートしてまいります。これからも、お客様の信頼に応えられるよう、誠実なものづくりを続けてまいります。
                </p>
                <p className="font-medium text-[#1A4D6D] pt-4">
                  おかやま健康製薬株式会社
                  <br />
                  代表取締役　岡山 太郎
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}